import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { getDeliverers, getDeliverersBySuperadmin } from "../../api/userApi";
import { getZones } from "../../api/zonesApi";
import { notification } from "../../utils/Notifications";
import DeliverersTable from "../../components/Deliverers/DeliverersTable";
import Title from "../../components/SharedComponents/Title";
import SearchBar from "../../components/SharedComponents/SearchBar";
import useLogout from "../../hooks/auth/authHook.js";
import styles from "./deliverersStyles.js";

const Deliverers = () => {
  const [logout] = useLogout();
  const { extraInformation } = useSelector((state) => state.user);
  const isSuperAdmin = extraInformation?.id_role === 5;

  const [deliverers, setDeliverers] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null); // null = "Todas las zonas"
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const hasFetchedInitial = useRef(false);

  const fetchDeliverers = () => {
    setIsLoading(true);

    const fetchFunction = isSuperAdmin
      ? getDeliverersBySuperadmin
      : getDeliverers;
    const fetchParams = isSuperAdmin
      ? selectedZone
        ? [Number(selectedZone)]
        : null
      : undefined;

    fetchFunction(fetchParams)
      .then((response) => {
        if (response.data.status_Code === 200) {
          setDeliverers(response.data.info_dealers || []);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener los repartidores",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchZones = () => {
    getZones()
      .then((response) => {
        if (response.data.status_Code === 200) {
          setZones(response.data.ls_zones || []);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener las zonas",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      });
  };

  const handleZoneChange = (event) => {
    const zoneValue = event.target.value;
    setSelectedZone(zoneValue === "" ? null : zoneValue);
  };

  useEffect(() => {
    fetchZones();
  }, []);

  useEffect(() => {
    // Solo hacer fetch si ya tenemos el rol del usuario y no hemos hecho fetch antes
    if (extraInformation?.id_role !== undefined && !hasFetchedInitial.current) {
      hasFetchedInitial.current = true;
      fetchDeliverers();
    }
  }, [extraInformation?.id_role]);

  // Actualizar repartidores cuando cambie la zona (solo para SuperAdmin, después del fetch inicial)
  useEffect(() => {
    if (
      isSuperAdmin &&
      extraInformation?.id_role === 5 &&
      hasFetchedInitial.current
    ) {
      fetchDeliverers();
    }
  }, [selectedZone]);

  return (
    <Container style={styles.mainContainer}>
      <Title title="Repartidores" />
      <SearchBar setSearch={setSearch} />

      {isSuperAdmin && (
        <Container style={{ marginBottom: "16px", marginTop: "16px" }}>
          <FormControl variant="standard" style={{ minWidth: 200 }}>
            <InputLabel id="zone-filter-label">Filtrar por zona</InputLabel>
            <Select
              labelId="zone-filter-label"
              id="zone-filter"
              value={selectedZone || ""}
              onChange={handleZoneChange}
            >
              <MenuItem value="">
                <em>Todas las zonas</em>
              </MenuItem>
              {zones.map((zone) => {
                const zoneId =
                  typeof zone === "string"
                    ? zone
                    : zone.id_zone || zone.id || zone.zone_code;
                const zoneName =
                  typeof zone === "string"
                    ? zone
                    : zone.zone_name || zone.name || zone.zone_code || zone.id;
                return (
                  <MenuItem key={zoneId} value={zoneId}>
                    {zoneName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Container>
      )}

      <DeliverersTable
        deliverers={deliverers}
        isLoading={isLoading}
        search={search}
      />
    </Container>
  );
};

export default Deliverers;
